import { useCallback } from "react";
import { toast } from "sonner";
import { extractSentences } from "@/lib/sentence-stream";
import { createWordPacer } from "@/lib/word-pacer";
import { readConsent as readLocationConsent } from "./useExactLocation";

/**
 * Read the freshest exact-location headers straight from localStorage
 * at request-time (rather than closing over hook state) so the SSE
 * fetch always sees the user's most recent share/clear action.
 */
function _readExactLocationHeaders() {
  try {
    if (!readLocationConsent()) return {};
    const raw = localStorage.getItem("rammy_exact_location");
    if (!raw) return {};
    const data = JSON.parse(raw);
    if (!data || typeof data.lat !== "number" || typeof data.lng !== "number") return {};
    // Match STALE_MS in useExactLocation.js
    if (!data.ts || Date.now() - data.ts > 15 * 60 * 1000) return {};
    return {
      "X-User-Lat": String(data.lat),
      "X-User-Lng": String(data.lng),
      "X-User-Accuracy": String(Math.round(data.accuracy || 0)),
    };
  } catch { return {}; }
}

/**
 * Lightweight client-side mirror of the backend's `_should_web_search`
 * heuristic — used only to decide whether to preload the "one sec, let me
 * check" voice filler while the server does its Tavily round-trip. Errs on
 * the side of "yes, play the filler" because a false positive just plays a
 * warm interjection the user won't mind, while a false negative leaves
 * awkward silence.
 */
function _looksLikeLookup(text) {
  if (!text) return false;
  const t = String(text).trim();
  if (t.length < 6) return false;
  const l = t.toLowerCase();
  // Rule out obvious chit-chat / personal reflection
  if (/^(hi|hey|hello|thanks|thank you|sorry|yes|no|ok|okay)\b/.test(l)) return false;
  if (/^(i (feel|am|was|had|need|want)|my )/.test(l)) return false;
  // Two consecutive Capitalised tokens ≈ named entity
  if (/(?:^|[^A-Za-z])[A-Z][a-z][A-Za-z'’]{1,20}\s+[A-Z][a-z][A-Za-z'’]{1,20}/.test(t)) return true;
  // Wh-word opener with enough words to be substantive
  if (/^\s*(who|what|when|where|why|how|which)\b/.test(l) && l.split(/\s+/).length >= 3) return true;
  // Strong temporal / news markers
  if (/\b(latest|today|current|recent|recently|breaking|news|update|updates|new|released|releasing|upcoming|price|score|winner|champion)\b/.test(l)) return true;
  return false;
}

/**
 * useChatStream — factory for the chat's SSE `send()` function.
 *
 * Bundles together the whole request lifecycle:
 *   1. Push a user bubble (unless this is a `verify` re-run).
 *   2. Push a streaming assistant placeholder.
 *   3. POST to `/api/chat/stream` and parse the SSE frames.
 *   4. Route each event kind (delta / sources / compute / generate /
 *      trace / memories / error / done) to the right state update.
 *   5. In voice mode, stream sentences into the TTS queue as they finish.
 *
 * Because the parser depends on nearly the entire chat state, this hook
 * takes many dependencies rather than owning state itself. That preserves
 * the single source of truth in the ChatScreen component.
 */
export default function useChatStream({
  input, setInput,
  msgs: _msgs, setMsgs,
  convId, setConvId, convIdRef,
  attachments, setAttachments,
  setBusy, setExpression,
  setSavedMems,
  voiceRef, voiceModeRef,
  focusModeRef,   // optional ref → current focus_mode: "walking" | "focus" | null
  handleInlineGenerate,
}) {
  const send = useCallback(async (overrideText, opts = {}) => {
    const { verify = false } = opts;
    const text = (overrideText ?? input).trim();
    if ((!text && attachments.length === 0)) return;

    if (!overrideText && !verify) {
      setInput("");
      const el = document.querySelector('[data-testid="chat-input"]');
      if (el) el.style.height = "";
    }
    const currentAttachments = verify ? [] : attachments;
    if (!verify) setAttachments([]);

    setMsgs(m => [
      ...m,
      ...(verify ? [] : [{
        role: "user",
        content: text,
        images: currentAttachments.filter(a => a.kind !== "doc").map(a => ({ src: a.preview, dataUrl: `data:${a.mime};base64,${a.data}` })),
        docs: currentAttachments.filter(a => a.kind === "doc").map(a => ({ name: a.name, kind: a.docKind, size: a.size })),
      }]),
      { role: "assistant", content: "", streaming: true, verified: verify },
    ]);
    setBusy(true);
    setExpression("thinking");
    let started = false;
    let ttsBuffer = "";
    const inVoice = voiceModeRef.current;

    // Word-level reveal — 28–45ms/word, jittered. Reads as thinking rather
    // than as a teletype. TTS still receives the raw delta immediately, so
    // the pacer only shapes what the eye sees.
    const appendToBubble = (chunk) => {
      setMsgs(m => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last?.streaming) copy[copy.length - 1] = { ...last, content: (last.content || "") + chunk };
        return copy;
      });
    };
    const pacer = createWordPacer(appendToBubble);

    // Voice mode UX — as soon as we dispatch a message that LOOKS like a
    // real-world lookup, kick off a short "one sec, let me check" filler in
    // Rammy's voice so the user doesn't hear silence during the Tavily
    // round-trip. Heuristic mirrors the backend's `_should_web_search`
    // liberally — a false positive just plays a warm interjection nobody
    // minds; a false negative leaves the old silence-then-answer behaviour.
    if (inVoice && voiceRef.current?.playFiller && _looksLikeLookup(text)) {
      try { voiceRef.current.playFiller("checking"); } catch (_) { /* ignore */ }
    }

    try {
      // Watchdog — abort the SSE if no bytes arrive for STREAM_STALL_MS.
      // This kills the "spinner spins forever" failure mode when the server
      // silently drops the connection mid-stream (upstream LLM timeout,
      // load-balancer idle-timeout, browser suspended tab, etc.).
      const STREAM_STALL_MS = 90_000;
      const controller = new AbortController();
      let lastActivityAt = Date.now();
      const watchdogId = setInterval(() => {
        if (Date.now() - lastActivityAt > STREAM_STALL_MS) {
          try { controller.abort(); } catch (_) { /* ignore */ }
        }
      }, 5_000);
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/stream`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          // Send the user's IANA timezone on every request so the backend
          // never has to ask "what country / timezone are you in?" — the
          // LLM injects this into a USER CONTEXT block automatically.
          "X-User-Timezone": (() => {
            try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ""; }
            catch (_) { return ""; }
          })(),
          // Exact GPS coordinates — only present when the user has
          // tapped the "share exact location" button and the stored
          // fix hasn't expired. Spread at the end so it overrides the
          // timezone-only default.
          ..._readExactLocationHeaders(),
        },
        signal: controller.signal,
        body: JSON.stringify({
          message: text,
          // Always read from the ref so a rapid second voice turn (or a
          // barge-in-and-retalk) sends the RIGHT conversation_id even if
          // the first turn's meta event hasn't fully flowed through React
          // state yet.
          conversation_id: convIdRef?.current ?? convId,
          attachments: currentAttachments.map(a => ({ data: a.data, mime: a.mime, filename: a.name })),
          verify,
          voice: inVoice,
          focus_mode: focusModeRef?.current || undefined,
        }),
      });
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";

      try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        lastActivityAt = Date.now();  // any byte counts — resets the watchdog
        buf += decoder.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop() || "";
        for (const evt of events) {
          const lines = evt.split("\n");
          let name = "message", data = "";
          for (const l of lines) {
            if (l.startsWith("event: ")) name = l.slice(7).trim();
            else if (l.startsWith("data: ")) data += l.slice(6);
          }
          if (!data) continue;
          try {
            const payload = JSON.parse(data);
            if (name === "meta" && payload.conversation_id) {
              // Update the ref SYNCHRONOUSLY so the next send() (which may
              // fire before React commits the state update) reads the
              // correct id. Then also setConvId so the UI reacts.
              if (convIdRef) convIdRef.current = payload.conversation_id;
              setConvId(payload.conversation_id);
            }
            // Meta event may carry per-turn credibility summary (SEC 0.99,
            // Tavily 0.60, etc). Attach to the streaming assistant bubble
            // so <SourceBadge/> can render "🟢 3 sources · high confidence".
            if (name === "meta" && payload.credibility) {
              setMsgs(m => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                if (last?.streaming) copy[copy.length - 1] = { ...last, credibility: payload.credibility };
                return copy;
              });
            }
            if (name === "sources" && Array.isArray(payload.sources)) {
              setMsgs(m => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                if (last?.streaming) copy[copy.length - 1] = { ...last, sources: payload.sources };
                return copy;
              });
            }
            if (name === "delta" && payload.content) {
              // In voice mode we defer expression flip to VoiceMode's onplaying.
              if (!started) {
                started = true;
                if (!inVoice) setExpression("speaking");
              }
              pacer.push(payload.content);
              if (inVoice && voiceRef.current) {
                ttsBuffer += payload.content;
                const { sentences, remainder } = extractSentences(ttsBuffer);
                if (sentences.length > 0) {
                  for (const s of sentences) {
                    try { voiceRef.current.queueSpeak(s); } catch (_) { /* ignore */ }
                  }
                  ttsBuffer = remainder;
                }
              }
            }
            if (name === "compute" && payload) {
              setMsgs(m => {
                const copy = [...m];
                for (let k = copy.length - 1; k >= 0; k--) {
                  const b = copy[k];
                  if (b.role === "assistant") {
                    copy[k] = { ...b, computes: [...(b.computes || []), payload] };
                    break;
                  }
                }
                return copy;
              });
            }
            if (name === "generate" && payload && payload.kind && payload.prompt) {
              // Any images on this message ride along as reference pictures, so
              // "put me on a beach" works on the photo you just attached.
              const refs = (currentAttachments || [])
                .filter(a => a.kind === "image" && a.data)
                .slice(0, 4)
                .map(a => a.data);
              setTimeout(() => handleInlineGenerate(payload, refs), 0);
            }
            if (name === "reminder" && payload && payload.id && payload.title) {
              // Server-created reminder from <generate kind="reminder"/> —
              // attach it to the last assistant bubble so the confirmation
              // card renders inline.
              setMsgs(m => {
                const copy = [...m];
                for (let k = copy.length - 1; k >= 0; k--) {
                  const b = copy[k];
                  if (b.role === "assistant") {
                    copy[k] = { ...b, reminderCreated: payload };
                    break;
                  }
                }
                return copy;
              });
              toast.success("Reminder set");
            }
            if (name === "weather" && payload && payload.city) {
              setMsgs(m => {
                const copy = [...m];
                for (let k = copy.length - 1; k >= 0; k--) {
                  if (copy[k].role === "assistant") {
                    copy[k] = { ...copy[k], weather: payload };
                    break;
                  }
                }
                return copy;
              });
            }
            if (name === "calendar_event" && payload && payload.id) {
              // Rammy put a real event on the calendar. One-hour Undo.
              setMsgs(m => {
                const copy = [...m];
                for (let k = copy.length - 1; k >= 0; k--) {
                  if (copy[k].role === "assistant") {
                    copy[k] = { ...copy[k], calendarEvent: payload };
                    break;
                  }
                }
                return copy;
              });
              toast.success("On your calendar");
            }
            if (name === "calendar_event_error" && payload) {
              toast.error(payload.message || "Couldn't add that to your calendar.");
            }
            if (name === "commitment" && payload && payload.id) {
              setMsgs(m => {
                const copy = [...m];
                for (let k = copy.length - 1; k >= 0; k--) {
                  if (copy[k].role === "assistant") {
                    copy[k] = { ...copy[k], commitment: payload };
                    break;
                  }
                }
                return copy;
              });
              toast.success("I'll hold you to it");
            }
            if (name === "gmail_draft" && payload && payload.to) {
              // Server-created Gmail draft — attach it to the last assistant
              // bubble so the DraftCard renders inline with a Send button.
              setMsgs(m => {
                const copy = [...m];
                for (let k = copy.length - 1; k >= 0; k--) {
                  const b = copy[k];
                  if (b.role === "assistant") {
                    copy[k] = { ...b, gmailDraft: payload };
                    break;
                  }
                }
                return copy;
              });
              toast.success("Draft saved in Gmail");
            }
            if (name === "gmail_draft_error" && payload) {
              toast.error(payload.message || "Couldn't create the draft.");
            }
            if (name === "trace" && payload) {
              setMsgs(m => {
                const copy = [...m];
                for (let k = copy.length - 1; k >= 0; k--) {
                  const b = copy[k];
                  if (b.role === "assistant") {
                    // If the live-tools pipeline surfaced web-search citations
                    // (e.g. Tavily results), mirror them onto the bubble so
                    // the existing sources chip renders — same UI used for
                    // /verify results, unified experience.
                    const liveSources = Array.isArray(payload.live_sources) ? payload.live_sources : [];
                    const nextSources = (b.sources && b.sources.length) ? b.sources : liveSources;
                    // Preserve any credibility already latched from the earlier
                    // meta event; fall back to the trace payload for turns
                    // where the meta event didn't carry it.
                    const credibility = b.credibility || payload.credibility || null;
                    copy[k] = { ...b, trace: payload, sources: nextSources, credibility };
                    break;
                  }
                }
                return copy;
              });
            }
            if (name === "progress" && payload && payload.label) {
              // Two consumers: the site-build stage narration (ArtifactBuildingCard
              // reads `progressLabel`) and the live-activity strip, which shows the
              // REAL source titles arriving before the first token.
              setMsgs(m => {
                const copy = [...m];
                for (let k = copy.length - 1; k >= 0; k--) {
                  const b = copy[k];
                  if (b.role === "assistant" && b.streaming) {
                    copy[k] = {
                      ...b,
                      progressLabel: payload.label,
                      progressStage: payload.stage,
                      progressSources: Array.isArray(payload.sources) ? payload.sources : b.progressSources,
                    };
                    break;
                  }
                }
                return copy;
              });
            }
            if (name === "memories" && Array.isArray(payload.saved) && payload.saved.length) {
              setSavedMems(payload.saved);
              setTimeout(() => setSavedMems([]), 6500);
            }
            if (name === "error" && payload.message) {
              toast.error(`Rammy hit a snag: ${payload.message}`);
              setExpression("sad");
              setMsgs(m => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                if (last?.streaming) {
                  copy[copy.length - 1] = {
                    role: "assistant",
                    content: `⚠️ ${payload.message}\n\nTry again in a moment.`,
                    error: true,
                  };
                }
                return copy;
              });
            }
            if (name === "done") {
              const finalText = payload.full || "";
              // Let the word pacer finish revealing what it's holding, THEN
              // settle the bubble on the canonical full text.
              pacer.end(() => {
                setMsgs(m => {
                  const copy = [...m];
                  const last = copy[copy.length - 1];
                  if (last?.streaming) copy[copy.length - 1] = { ...last, role: "assistant", content: finalText || last.content, streaming: false };
                  return copy;
                });
              });
              if (inVoice && voiceRef.current) {
                const tail = ttsBuffer.trim();
                if (tail.length >= 2) {
                  try { voiceRef.current.queueSpeak(tail); } catch (_) { /* ignore */ }
                }
                try { voiceRef.current.endStream(); } catch (_) { /* ignore */ }
                ttsBuffer = "";
              }
            }
          } catch (_) { /* ignore malformed SSE frame */ }
        }
      }
      } finally {
        clearInterval(watchdogId);
      }
      setExpression("happy");
      setTimeout(() => setExpression("idle"), 2500);
    } catch (e) {
      pacer.flush();
      toast.error("Rammy got quiet — try again.");
      setExpression("sad");
      setMsgs(m => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        // Explicitly flip `streaming:false` so the "Open in Canvas" chip
        // and other post-stream affordances can surface even when the SSE
        // stream fails or the `done` frame never arrives. Preserve any
        // partial content the model already produced — it's often usable.
        if (last?.streaming) copy[copy.length - 1] = {
          ...last,
          role: "assistant",
          content: last.content || "I'm having a quiet moment — try again in a bit.",
          streaming: false,
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  }, [
    input, setInput,
    attachments, setAttachments,
    setMsgs,
    convId, setConvId, convIdRef,
    setBusy, setExpression, setSavedMems,
    voiceRef, voiceModeRef, focusModeRef,
    handleInlineGenerate,
  ]);

  return send;
}
